import {useInfluencerQuery} from "@/api/influencerApi";
import FullPageLoader from "@/components/Common/FullPageLoader";
import NotFound from "@/components/Common/NotFound";
import InfluencerActiveCampaigns from "@/components/Influencer/InfluencerDetail/InfluencerActiveCampaigns";
import InfluencerHeader from "@/components/Influencer/InfluencerDetail/InfluencerHeader";
import InfluencerHighlights from "@/components/Influencer/InfluencerDetail/InfluencerHighlights";
import InfluencerPendingCampaigns from "@/components/Influencer/InfluencerDetail/InfluencerPendingCampaigns";
import InfluencerContent from "@/components/Influencer/InfluencerDetail/InfluencerRefluencedContent/InfluencerContent";
import InfluencerSocialSection from "@/components/Influencer/InfluencerDetail/InfluencerSocialSection";
import SimilarInfluencers from "@/components/Influencer/InfluencerDetail/SimilarInfluencers";
import {useLogout} from "@/hooks/useLogout";
import {useIsAdmin, useIsInfuencer} from "@/hooks/useUserHooks";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import useInfluencerStore from "@/store/influencer";
import useTokenStore from "@/store/token";
import useUserStore from "@/store/user";
import {createFileRoute, useRouter} from "@tanstack/react-router";
import {zodValidator} from "@tanstack/zod-adapter";
import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";
import {z} from "zod";

export const Route = createFileRoute("/influencer/$influencerId/")({
  component: InfluencerDetails,
  params: {
    parse: raw => ({influencerId: Number(raw.influencerId)}),
    stringify: params => ({influencerId: params.influencerId?.toString()}),
  },
  validateSearch: zodValidator(
    z.object({
      campaign_id: z.number().optional(),
      influencer_type: z.string().optional(),
      source_tab: z.string().optional(),
    }),
  ),
});

function InfluencerDetails() {
  const {t} = useTranslation("influencer_profile");
  const navigate = Route.useNavigate();
  const router = useRouter();
  const {influencerId} = Route.useParams();
  const isAdmin = useIsAdmin();
  const logout = useLogout();
  const influencer = useInfluencerStore(state => state.influencer);
  const isInfluencer = useIsInfuencer({excludeAdmins: true});
  const setValue = useInfluencerStore(state => state.setValue);
  const user = useUserStore(state => state.user);
  const userId = useTokenStore(state => state.userId);
  const showSocialFeed =
    influencer?.user.is_verified ||
    influencer?.user.tiktok_verified ||
    (isAdmin && (influencer?.user.username || influencer?.user.tiktok_username));

  const {data, error} = useInfluencerQuery({
    id: Number(influencerId),
    sourceParams: router.state.location.searchStr.slice(1),
    queryOptions: {
      refetchOnMount: true,
    },
  });

  useTitle(
    influencer?.user && influencer.user?.first_name
      ? t("at_refluenced", {name: influencer.user?.first_name})
      : "Refluenced",
  );

  useEffect(() => {
    if (data) {
      setValue("influencer", data);
      capturePostHogEvent(PostHogEvents.InfluencerProfileViewed, {influencerId: influencerId, source: "detail_page"});
    }
  }, [data]);
  useEffect(() => {
    if (!userId && ((influencer && !influencer.is_public) || error)) {
      navigate({to: "/login"});
    }

    if (error?.response?.status === 401 || error?.response?.status === 403) {
      logout();
    }
  }, [error, userId, influencer]);

  if (!!error) {
    return <NotFound message={t("influencer_not_found")} page={t("influencers")} redirect="/influencers-list" />;
  }

  if (!influencer) {
    return <FullPageLoader />;
  }

  return (
    <>
      <meta name="robots" content="noindex" />
      <div className="max-w-wide container mx-auto mt-8 mb-20 flex flex-col gap-12 px-8">
        <InfluencerHeader />
        <InfluencerHighlights />
        {(!isInfluencer || user.influencer_uuid === influencer.uuid) && (
          <>
            {showSocialFeed && <InfluencerSocialSection />}
            <InfluencerActiveCampaigns />
            <InfluencerPendingCampaigns />
            <InfluencerContent influencerId={influencer.uuid!} />
            <InfluencerContent influencerId={influencer.uuid!} filterByCampaignType="ugc" />
            <SimilarInfluencers />
          </>
        )}
      </div>
    </>
  );
}
